import {
  ChangeDetectorRef,
  Component,
  ViewEncapsulation,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnDestroy,
} from "@angular/core";
import { NgForm } from "@angular/forms";
import { StatusChange, StatusChangeHistory } from "@common/models";
import {
  AuthenticationService,
  ConfigurationService,
  StatusChangeHistoryService,
} from "@common/services";
import { IConfigTranslate } from "@core/models";
import { InsightsService, MSTranslationService } from "@core/services";
import { environment } from "environments/environment";
import { getTranslateValue } from "@core/utils";

import { NgbActiveModal } from "@ng-bootstrap/ng-bootstrap";
import { TranslateService } from "@ngx-translate/core";
import { CustomSwalService } from "app/helpers";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";

@Component({
    selector: "app-status-comments",
    templateUrl: "./status-comments.component.html",
    styleUrls: ["./status-comments.component.scss"],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class StatusCommentsComponent implements OnInit, OnDestroy {
  public approvalComments: string;
  public isSubmitted: boolean = false;
  public approvalList: StatusChangeHistory[] = [];
  public question: string = null;
  public approvalCommentsEnglish: string;
  public loading = false;

  private _unsubscribeAll: Subject<any>;

  @Input() title: string;
  @Input() objectId: number;
  @Input() objectName: string;
  @Input() statusChange: StatusChange;

  @Output() approvalEmitter: EventEmitter<{
    comment: string;
    isApproved: boolean;
    commentEnglish?:string
  }> = new EventEmitter();

  constructor(
    private _cdr: ChangeDetectorRef,
    private _customSwal: CustomSwalService,
    private _statusChangeHistoryService: StatusChangeHistoryService,
    private _authenticationService: AuthenticationService,
    public activeModal: NgbActiveModal,
    private _traslateService: TranslateService,
    private _msTranslateService:MSTranslationService,
    private _configurationService: ConfigurationService,
    private _insightsService: InsightsService
  ) {
    this._unsubscribeAll = new Subject();
   }

  ngOnInit(): void {


    this._customSwal.showLoading();
    const currentUser = this._authenticationService.currentUserValue;
    this._statusChangeHistoryService
      .getByMenuIdAndObjectId(this.statusChange.menu.id, this.objectId)
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe(
        (res: StatusChangeHistory[]) => {
          if (this.statusChange.newStatus) {
            this.approvalList = res.filter(
              (sCH) =>
                sCH.status.code != 'ANU' &&
                sCH.newStatus.id === this.statusChange.newStatus.id &&
                sCH.createdUser.id === currentUser.id &&
                sCH.isValid == true
            );
          }
          this._customSwal.close();
          if (this.approvalList.length > 0 && this.statusChange?.approvers > 1) {
            this.log('Intentó aprobar un cambio de estado que ya había aprobado', this.logData());
            this.activeModal.close();
            setTimeout(() => {
              this._traslateService.get(['COMMON.STATUS_COMMENTS.MESSAGE.DUPLICATE'],['COMMON.ACTIONS.OOPS']).pipe(takeUntil(this._unsubscribeAll)).subscribe(value => {
                this._customSwal.showErrorMsg(value['COMMON.STATUS_COMMENTS.MESSAGE.DUPLICATE'],
                value['COMMON.ACTIONS.OOPS']);
            });
            }, 300);
          }
          this._cdr.detectChanges();
        },
        (err) => {
          this.log('Error al cargar el historial de aprobaciones', this.logData(), err, true);
          this._customSwal.close();
          this._traslateService.get(['COMMON.APPROVAL_LIST.MESSAGE.ERROR_LOAD'],['COMMON.ACTIONS.OOPS']).pipe(takeUntil(this._unsubscribeAll)).subscribe(value => {
            this._customSwal.showErrorMsg(value['COMMON.APPROVAL_LIST.MESSAGE.ERROR_LOAD'],
            value['COMMON.ACTIONS.OOPS']);
        }); 
        }
      );
    //Get the current LANG
    let lang = this._traslateService.currentLang;
    let obj: any;
    obj = {
      obj: this.statusChange,
      property: "title"
    };
    //send the status to translate.
    let status = getTranslateValue(this.statusChange.title, obj, lang);

    Promise.resolve().then(() => {
      this._traslateService.get('COMMON.STATUS_COMMENTS.MESSAGE.QUESTION',
        { statusChange_title: status, objectName: this.objectName }).pipe(takeUntil(this._unsubscribeAll)).subscribe(value => {
          this.question = value;
          this._cdr.detectChanges();
        });
    });
  }


  /**
   * On Destroy
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscription
    this._unsubscribeAll.next(null);
    this._unsubscribeAll.complete();
  }

  onReject(form: NgForm) {
    this.emitApproval(false, form);
  }

  onSubmit(form: NgForm) {
    this.emitApproval(true, form);
  }

  /**
   * El subtítulo repite el nombre del objeto solo si el título no lo trae ya
   * (status-change-modal arma el título como "estado - nombre")
   */
  get showSubtitle(): boolean {
    return !!this.objectName && !(this.title ?? '').includes(this.objectName);
  }

  /**
   * Cierra sin aprobar ni rechazar (X y botón Cerrar)
   */
  cancel(): void {
    this.log('Cerró el cambio de estado sin confirmar', this.logData());
    this.activeModal.close('Cross click');
  }

  emitApproval(isApproved: boolean, form: NgForm) {
    if (!form.valid) return;
    this.log(isApproved ? 'Aceptó un cambio de estado' : 'Rechazó un cambio de estado', this.logData());
    this.approvalEmitter.emit({ isApproved, comment: this.approvalComments,commentEnglish:this.approvalCommentsEnglish });
    this.activeModal.close();
  }

  /**
   * Función para traducir inputs
   * @param ngModelName nombre de ngModel 
   * @param ngModelValue valor obtenido del ngModel   
   */
  translateInput(ngModelName: string, ngModelValue: any,fromLanguage: string = "", toLanguage: string = "") {

    //verificamos que el input contenga texto (antes también traducía vacíos)
    if (typeof ngModelValue === 'string' && ngModelValue.trim().length > 0) {
      this.loading = true;
      //realizamos la petición y enviamos texto a traducir
      this._msTranslateService.getTranslation(ngModelValue,fromLanguage,toLanguage).then(value => {
          Promise.resolve().then(() => {
            this.approvalCommentsEnglish = (ngModelName == 'approvalComments') ? value : this.approvalCommentsEnglish;
            this.approvalComments = (ngModelName == 'approvalCommentsEn') ? value : this.approvalComments;
            this.loading = false;
            this._cdr.detectChanges();
          });
      }).catch(error => {
          // Sin esto los botones quedaban deshabilitados para siempre
          this.log('Error al traducir el comentario del cambio de estado', { ...this.logData(), from: fromLanguage || 'es' }, error, true);
          Promise.resolve().then(() => {
            this.loading = false;
            this._cdr.detectChanges();
          });
      });
    }
  }

  /**
   * Contexto del cambio de estado para las trazas (ids y códigos, no el texto)
   */
  private logData(): any {
    return {
      objectId: this.objectId ?? null,
      menuId: this.statusChange?.menu?.id ?? null,
      statusChangeId: this.statusChange?.id ?? null,
      newStatus: this.statusChange?.newStatus?.code ?? null,
    };
  }

  /**
   * Manda una traza a Application Insights, etiquetada con el proyecto y el
   * usuario que la originó.
   */
  private log(message: string, data: any, ex: any = null, error: boolean = false): void {
    const user = this._authenticationService.currentUserValue;

    if (error) {
      this._insightsService.traceError(environment.projects.StatusChange, message, data, ex, user);
      return;
    }

    this._insightsService.traceInfo(environment.projects.StatusChange, message, data, user);
  }
}
